// Thin typed client for the middleware worker.
// Auth: login/signup return a session token (JWT in multi-user mode) stored in
// localStorage and sent as `Authorization: Bearer <token>`.
import type {
  AddCategoryResponse,
  AddSubcategoryResponse,
  AISettings,
  AISettingsResponse,
  AnalyticsResponse,
  CategoriesResponse,
  CategorizeGroup,
  CategorizeGroupsResponse,
  DeleteCategoryResponse,
  DeleteSourceResponse,
  DeleteTransactionResponse,
  HealthResponse,
  IngestEvent,
  IngestResult,
  LearnCategoryResponse,
  LearnedRule,
  LearnedRulesResponse,
  LoginResponse,
  MemberInfo,
  MemberMutationResponse,
  MembersResponse,
  RecoverResponse,
  SignupResponse,
  SourcesResponse,
  Subcategory,
  SuggestionsResponse,
  Timeframe,
  TransactionsResponse,
  UncategorizedGroup,
  UncategorizedResponse,
  UserInfo,
} from './types';

// Same-origin proxy via Pages Function — no Tailscale URL in the browser.
// Set VITE_API_URL to override (e.g. for local dev with Vite proxy).
const API_BASE: string = ((import.meta.env.VITE_API_URL as string | undefined) ?? '').replace(
  /\/$/,
  '',
);

const TOKEN_STORAGE = 'fintrack_token';
const USER_STORAGE = 'fintrack_user';

/** Dispatched when the middleware returns 401 (session expired/invalid). */
export const UNAUTHORIZED_EVENT = 'fintrack:unauthorized';

// ---------------------------------------------------------------------------
// Token + user persistence
// ---------------------------------------------------------------------------

export function getToken(): string {
  return localStorage.getItem(TOKEN_STORAGE) ?? '';
}
export function setToken(token: string): void {
  if (token) localStorage.setItem(TOKEN_STORAGE, token);
  else localStorage.removeItem(TOKEN_STORAGE);
}
export function hasToken(): boolean {
  return getToken().length > 0;
}
export function getUser(): UserInfo | null {
  try {
    return JSON.parse(localStorage.getItem(USER_STORAGE) ?? 'null') as UserInfo | null;
  } catch {
    return null;
  }
}
export function setUser(user: UserInfo | null): void {
  if (user) localStorage.setItem(USER_STORAGE, JSON.stringify(user));
  else localStorage.removeItem(USER_STORAGE);
}
export function logout(): void {
  setToken('');
  setUser(null);
}

/** Persist the login/signup response and return the user. */
function persistSession(data: LoginResponse): UserInfo {
  setToken(data.token ?? '');
  const user = data.user ?? {
    id: 'service',
    name: 'Head',
    username: 'service',
    role: 'head' as const,
    familyId: 'service',
    tag: '',
  };
  setUser(user);
  return user;
}

function authHeaders(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type') && init?.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, authHeaders(init));
  } catch {
    throw new Error(
      'Cannot reach the analysis API. Is the middleware worker running? ' +
        (API_BASE ? `(${API_BASE})` : '(set VITE_API_URL or start the Vite dev proxy)'),
    );
  }

  if (res.status === 401) {
    // Session expired / invalid — clear credentials and let App reopen login.
    setToken('');
    setUser(null);
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new Error('Session expired or invalid — please log in again.');
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const parsed = (await res.json()) as {
        error?: string;
        message?: string;
        details?: string;
        hint?: string;
      };
      message = parsed.error ?? parsed.message ?? message;
      if (parsed.details) message += ` — ${parsed.details}`;
      if (parsed.hint) message += ` (${parsed.hint})`;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** POST /api/auth/login → stores the token + user on success. */
export async function login(username: string, password: string): Promise<UserInfo> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = (await res.json().catch(() => ({ ok: false, error: 'Invalid server response' }))) as LoginResponse;
  if (!res.ok || !data.ok || !data.token) {
    throw new Error(data.error ?? `Login failed (${res.status})`);
  }
  const user = persistSession(data);
  return user;
}

/** POST /api/auth/signup — creates the family head (first account only).
 *  Returns { user, token, recoveryKey }. The recoveryKey is shown ONCE.
 */
export async function signup(name: string, username: string, password: string): Promise<{ user: UserInfo; recoveryKey?: string }> {
  const res = await fetch(`${API_BASE}/api/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, username, password }),
  });
  const data = (await res.json().catch(() => ({ ok: false, error: 'Invalid server response' }))) as SignupResponse;
  if (!res.ok || !data.ok || !data.token) {
    throw new Error(data.error ?? `Signup failed (${res.status})`);
  }
  const user = persistSession(data);
  return { user, recoveryKey: data.recoveryKey };
}

/** POST /api/me/change-password — self-service password change. */
export function changePassword(oldPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/me/change-password', {
    method: 'POST',
    body: JSON.stringify({ oldPassword, newPassword }),
  });
}

/** POST /api/auth/recover — forgot-password via recovery key (no old password needed). */
export function recoverPassword(username: string, recoveryKey: string, newPassword: string): Promise<RecoverResponse> {
  return fetch(`${API_BASE}/api/auth/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, recoveryKey, newPassword }),
  }).then(async (res) => {
    const data = (await res.json().catch(() => ({ ok: false, error: 'Invalid server response' }))) as RecoverResponse;
    if (!res.ok || !data.ok) {
      throw new Error(data.error ?? `Recovery failed (${res.status})`);
    }
    return data;
  });
}

// ---------------------------------------------------------------------------
// Family admin (head only)
// ---------------------------------------------------------------------------

export function fetchMembers(): Promise<MembersResponse> {
  return request<MembersResponse>('/api/admin/users');
}

export function createMember(name: string, username: string, password: string): Promise<MemberMutationResponse> {
  return request<MemberMutationResponse>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ name, username, password }),
  });
}

export function resetMemberPassword(id: string, password: string): Promise<MemberMutationResponse> {
  return request<MemberMutationResponse>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ password }),
  });
}

/** Promote/demote a family user (head or co-head only). */
export function setMemberRole(id: string, role: 'co-head' | 'member'): Promise<MemberMutationResponse> {
  return request<MemberMutationResponse>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export function deleteMember(id: string): Promise<{ ok: boolean; error?: string }> {
  return request<{ ok: boolean; error?: string }>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Data endpoints
// ---------------------------------------------------------------------------

export function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/api/health');
}

/** `tag` scopes to one member (head view); null/undefined = family-wide. */
export function fetchAnalytics(timeframe: Timeframe, tag?: string | null, account?: string | null): Promise<AnalyticsResponse> {
  const params = new URLSearchParams();
  params.set('timeframe', timeframe);
  if (tag) params.set('tag', tag);
  if (account) params.set('account', account);
  return request<AnalyticsResponse>(`/api/analytics?${params.toString()}`);
}

export async function fetchSuggestions(): Promise<SuggestionsResponse | null> {
  const res = (await request<Record<string, unknown>>('/api/suggestions')) as
    | (SuggestionsResponse & Record<string, unknown>)
    | null;
  // /api/suggestions may return a 202 {status:"processing"} or an error payload
  // that is truthy but lacks the insights arrays — treat those as "no data yet".
  if (!res || !Array.isArray(res.spending_traps) || !Array.isArray(res.investment_suggestions)) {
    return null;
  }
  return res;
}

/**
 * Send extracted PDF/markdown text to the middleware → AI → Firefly.
 * The response is a stream of NDJSON progress events; `onEvent` is called
 * for each stage and the final `done` event is resolved.
 */
export async function ingestMarkdown(
  text: string,
  fileName: string,
  onEvent?: (ev: IngestEvent) => void,
  accountNumber?: string | null,
): Promise<IngestResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/ingest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ text, source: fileName, ...(accountNumber ? { accountNumber } : {}) }),
    });
  } catch {
    throw new Error(
      'Cannot reach the analysis API. Is the middleware worker running? ' +
        (API_BASE ? `(${API_BASE})` : '(set VITE_API_URL or start the Vite dev proxy)'),
    );
  }

  if (res.status === 401) {
    setToken('');
    setUser(null);
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new Error('Session expired or invalid — please log in again.');
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `Upload failed (${res.status})`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Upload failed: no response stream');
  const decoder = new TextDecoder();
  let buffer = '';
  let final: IngestResult | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      let ev: IngestEvent;
      try {
        ev = JSON.parse(line) as IngestEvent;
      } catch {
        continue; // ignore stray lines
      }
      if (ev.stage === 'error') {
        throw new Error(String(ev.message ?? 'The statement could not be processed.'));
      }
               if (ev.stage === 'done') {
                 final = ev as unknown as IngestResult;
               } else if (ev.stage === 'duplicate') {
                 // A skipped duplicate is a successful (no-op) completion, not an error.
                 final = ev as unknown as IngestResult;
               }
      onEvent?.(ev);
    }
  }

  if (!final) {
    throw new Error('Upload interrupted — no completion event received.');
  }
  return final;
}

/** Recent transactions (verification/debugging). */
export function fetchTransactions(limit = 200, account?: string | null): Promise<TransactionsResponse> {
  const q = account ? `&account=${encodeURIComponent(account)}` : '';
  return request<TransactionsResponse>(`/api/transactions?limit=${limit}${q}`);
}

/** Distinct bank account numbers seen in the data, with transaction counts. */
export function fetchAccounts(): Promise<{ accounts: Array<{ number: string; count: number }> }> {
  return request<{ accounts: Array<{ number: string; count: number }> }>('/api/transactions/accounts');
}

// ---------------------------------------------------------------------------
// Manual categorization (learned rules)
// ---------------------------------------------------------------------------

/** All merchant groups with their current category (so the user can review AND fix). */
export function fetchCategorizeGroups(onlyOther = false): Promise<CategorizeGroupsResponse> {
  return request<CategorizeGroupsResponse>(`/api/categorize/groups${onlyOther ? '?only=other' : ''}`);
}

/** Built-in + family custom categories. */
export function fetchCategories(): Promise<CategoriesResponse> {
  return request<CategoriesResponse>('/api/categorize/categories');
}

/** Create a new custom category for the family. */
export function addCategory(name: string): Promise<AddCategoryResponse> {
  return request<AddCategoryResponse>('/api/categorize/categories', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

/** Transactions stuck in "Other" grouped by merchant, so the user can fix them. */
export function fetchUncategorized(): Promise<UncategorizedResponse> {
  return request<UncategorizedResponse>('/api/categorize/uncategorized');
}

/** The family's learned merchant→category rules. */
export function fetchLearnedRules(): Promise<LearnedRulesResponse> {
  return request<LearnedRulesResponse>('/api/categorize/rules');
}

/**
 * Teach the app a category for a merchant: saves the rule AND re-categorizes
 * every existing matching transaction (returns how many were updated).
 */
export function learnCategory(
  merchant: string,
  category: string,
): Promise<LearnCategoryResponse> {
  return request<LearnCategoryResponse>('/api/categorize/learn', {
    method: 'POST',
    body: JSON.stringify({ merchant, category }),
  });
}

// ---------------------------------------------------------------------------
// Hierarchical category system (prime + subcategories)
// ---------------------------------------------------------------------------

/** Fetch complete category hierarchy — 5 prime categories with their subcategories */
export function fetchCategoriesHierarchy(): Promise<{
  primeCategories: import('./types').PrimeCategory[];
  subcategories: import('./types').Subcategory[];
  custom: string[];
}> {
  return request<{
    primeCategories: import('./types').PrimeCategory[];
    subcategories: import('./types').Subcategory[];
    custom: string[];
  }>('/api/categorize/hierarchy');
}

/** Fetch all merchant groups with prime category info */
export function fetchCategorizeGroupsV2(
  primeCategoryId?: string,
  searchQuery?: string,
): Promise<CategorizeGroupsResponse> {
  const params = new URLSearchParams();
  if (primeCategoryId) params.set('prime', primeCategoryId);
  if (searchQuery) params.set('q', searchQuery);
  const qs = params.toString();
  return request<CategorizeGroupsResponse>(`/api/categorize/groups${qs ? '?' + qs : ''}`);
}

/** Create a new custom subcategory under a prime category */
export function createSubcategory(
  parentId: string,
  name: string,
): Promise<AddSubcategoryResponse> {
  return request<AddSubcategoryResponse>('/api/categorize/subcategory', {
    method: 'POST',
    body: JSON.stringify({ parentId, name }),
  });
}

/** Delete a custom subcategory */
export function deleteSubcategory(
  subcategoryId: string,
): Promise<DeleteCategoryResponse> {
  return request<DeleteCategoryResponse>(`/api/categorize/subcategory/${encodeURIComponent(subcategoryId)}`, {
    method: 'DELETE',
  });
}

/** Merge one category into another (removes the redundant category). */
export function renameCategory(
  from: string,
  to: string,
): Promise<{ ok: boolean; from: string; to: string; updated: number; rulesChanged: number; error?: string }> {
  return request<{ ok: boolean; from: string; to: string; updated: number; rulesChanged: number; error?: string }>('/api/categorize/rename', {
    method: 'POST',
    body: JSON.stringify({ from, to }),
  });
}

/** Get the most recent merge (so it can be reversed). */
export function fetchLastMerge(): Promise<{ merge: { from: string; to: string; count: number; at: number } | null }> {
  return request<{ merge: { from: string; to: string; count: number; at: number } | null }>('/api/categorize/last-merge');
}

/** Reverse the most recent merge (demerge). */
export function undoMerge(): Promise<{ ok: boolean; from: string; to: string; restored: number; error?: string }> {
  return request<{ ok: boolean; from: string; to: string; restored: number; error?: string }>('/api/categorize/undo-merge', {
    method: 'POST',
  });
}

/** Smart search across prime categories and subcategories */
export function searchCategoriesHierarchy(
  query: string,
): Promise<{ results: import('./types').PrimeCategory[] | import('./types').Subcategory[]; total: number }> {
  return request<{ results: import('./types').PrimeCategory[] | import('./types').Subcategory[]; total: number }>('/api/categorize/search', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

export type {
  MemberInfo,
  LearnedRule,
  UncategorizedGroup,
  LearnedRulesResponse,
  LearnCategoryResponse,
  UncategorizedResponse,
  CategorizeGroup,
  CategorizeGroupsResponse,
  CategoriesResponse,
  Subcategory,
};

export type { PrimeCategory } from './types';

// ---------------------------------------------------------------------------
// Source (PDF) management
// ---------------------------------------------------------------------------

/** List all uploaded PDFs/sources with transaction counts. */
export function fetchSources(): Promise<SourcesResponse> {
  return request<SourcesResponse>('/api/sources');
}

/** Delete all transactions from a specific source (PDF). */
export function deleteSource(sourceName: string): Promise<DeleteSourceResponse> {
  return request<DeleteSourceResponse>(
    `/api/sources/${encodeURIComponent(sourceName)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Individual transaction deletion
// ---------------------------------------------------------------------------

/** Delete a single transaction by ID. */
export function deleteTransaction(transactionId: string): Promise<DeleteTransactionResponse> {
  return request<DeleteTransactionResponse>(
    `/api/transactions/${encodeURIComponent(transactionId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// AI Settings
// ---------------------------------------------------------------------------

/** Get current AI provider configuration. */
export function fetchAISettings(): Promise<AISettingsResponse> {
  return request<AISettingsResponse>('/api/settings/ai');
}

/** Update AI provider configuration. */
export function updateAISettings(settings: Partial<AISettings>): Promise<AISettingsResponse> {
  return request<AISettingsResponse>('/api/settings/ai', {
    method: 'PUT',
    body: JSON.stringify({ settings }),
  });
}

// ---------------------------------------------------------------------------
// Suggestions queue
// ---------------------------------------------------------------------------

/** Check suggestions queue status. */
export function fetchSuggestionsStatus(): Promise<{ status: string; message?: string; processedAt?: number }> {
  return request<{ status: string; message?: string; processedAt?: number }>('/api/suggestions/status');
}

/** Trigger a new suggestions generation. */
export function triggerSuggestions(): Promise<{ status: string; message?: string }> {
  return request<{ status: string; message?: string }>('/api/suggestions', {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Debug log (persisted server-side statements, surfaced in the UI)
// ---------------------------------------------------------------------------

export interface DebugEntry {
  ts: number;
  source: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  data?: unknown;
}

/** Fetch persisted debug statements from the worker. */
export function fetchDebug(): Promise<{ entries: DebugEntry[] }> {
  return request<{ entries: DebugEntry[] }>('/api/debug');
}

/** Clear the persisted debug log. */
export function clearDebug(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/debug', { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Firefly-native read proxy (accounts, budgets, bills, recurrences…)
// ---------------------------------------------------------------------------

/** GET a Firefly read endpoint via the worker proxy. */
export function fireflyGet<T = any>(path: string, query?: Record<string, string>): Promise<T> {
  const params = new URLSearchParams();
  if (query) for (const [k, v] of Object.entries(query)) params.set(k, v);
  const qs = params.toString();
  return request<T>(`/api/firefly/proxy/${path}${qs ? `?${qs}` : ''}`);
}

/** Create Firefly budgets from this period's WalletPulse spend. */
export async function syncFireflyBudgets(): Promise<{ ok: boolean; created?: string[]; count?: number; error?: string }> {
  return request<{ ok: boolean; created?: string[]; count?: number; error?: string }>(`/api/firefly/sync-budgets`, {
    method: 'POST',
  });
}

/** Set a Firefly account's opening balance (so its running balance is accurate). */
export async function setAccountOpeningBalance(id: string, openingBalance: number, date?: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/firefly/account-opening-balance`, {
    method: 'POST',
    body: JSON.stringify({ id, opening_balance: openingBalance, opening_balance_date: date }),
  });
}

/** Detect recurring debits and create Firefly bills for them. */
export async function syncFireflyBills(): Promise<{ ok: boolean; created?: Array<{ name: string; count: number; repeat_freq: string }>; skipped?: number; failed?: Array<{ name: string; error?: string }>; error?: string }> {
  return request<{ ok: boolean; created?: Array<{ name: string; count: number; repeat_freq: string }>; skipped?: number; failed?: Array<{ name: string; error?: string }>; error?: string }>(`/api/firefly/sync-bills`, {
    method: 'POST',
  });
}

/** Latest AI-derived insights for the family (persisted during ingest). */
export async function fetchAiInsights(): Promise<{
  insights: { spending_traps?: string[]; investment_suggestions?: string[]; provider?: string; at?: string } | null;
}> {
  return request<{ insights: { spending_traps?: string[]; investment_suggestions?: string[]; provider?: string; at?: string } | null }>(`/api/ai/insights`);
}
